import React, { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { CreateProductOrderReceiptDto, ProductOrderDto, ProductOrderStatus } from '@services/dtos'
import { Alert, Checkbox, FormControlLabel, FormHelperText, Grid, Typography } from '@mui/material'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import InputField from '@components/inputs/InputField'
import { MemberDto, MemberService } from '@services/api'
import { MemberRoleEnumLabels, ProductOrderStatusLabels } from '@utils/constants'
import { WrapperFormContent } from '../../WrapperFormContent'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import moment from 'moment'
import { SimpleFileInput } from '@components/inputs/SimpleFileInput'
import { removeProductOrderReceiptFile, setProductOrderReceiptFile } from '@state/reducers/productOrderReducer'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'

type Props = {
  useCreateForm: UseFormReturn<CreateProductOrderReceiptDto, object>
  productOrder: ProductOrderDto
}

const ProductOrderReceiptStepInfo: React.FC<Props> = ({ useCreateForm, productOrder }) => {
  const { watch, setValue, formState: { errors }, control } = useCreateForm
  const dispatch = useAppDispatch()
  const { deliveryNote, releaseCertificate } = useSelector(({ productOrder }) => ({
    deliveryNote: productOrder.productOrderReceiptFiles.deliveryNote,
    releaseCertificate: productOrder.productOrderReceiptFiles.releaseCertificate,
  }))

  const fields = [
    { name: 'externalSupplierName', label: 'Nom fournisseur externe', type: 'text', sm: 12 },
    { name: 'externalSupplierAddress', label: 'Adresse fournisseur externe', sm: 12 },
    { name: 'comment', label: 'Commentaire', type: 'textarea', sm: 12 },
    { name: 'errorComment', label: 'Commentaire en cas d\'erreur', type: 'textarea', sm: 12 },
    { name: 'courierFullName', label: 'Nom / prénom livreur', type: 'text', sm: 12 },
  ]

  const useOrderSupplier = watch('useOrderSupplier')
  const useConnectedMemberAsReceiver = watch('useConnectedMemberAsReceiver')
  const receivedAt = watch('receivedAt')

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const renderFields = (fields: any[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name} 
        className={field.name === 'externalSupplierAddress' ? '!pt-2' : field.name === 'externalSupplierName' ? '!pt-1' : ''}>
        <InputField
          key={field.name}
          control={control}
          name={field.name}
          label={field.label}
          disabled={useOrderSupplier && (field.name === 'externalSupplierName' || field.name === 'externalSupplierAddress')}
          type={field.type ? field.type : 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
        />
      </Grid>
    ))
  }

  const lineItems = watch('lineItems')
  useEffect(() => {
    const receivedUnitsByProductId = lineItems.reduce((acc, item) => {
      acc[item.productId] = (acc[item.productId] || 0) + (+item.receivedUnits || 0)
      return acc
    }, {} as Record<string, number>)
    const allReceived = productOrder.lineItems.every((item) => {
      const receivedUnits = receivedUnitsByProductId[item.product.id] || 0
      return !item.remainingQuantity || (receivedUnits >= item.remainingQuantity)
    })
    setValue('markOrderAsReceived', allReceived)
  }, [lineItems])

  return (
    <WrapperFormContent legend={'Informations complémentaires'} className="mt-4 pb-12">
      <Grid container columnSpacing={6} mt={2} alignItems="flex-start">
        <Grid container item xs={12} md={6} spacing={2}>
          <Grid item xs={12}>
            <DateTimePicker
              label="Date / heure réception"
              name="receivedAt"
              value={
                receivedAt
                  ? moment(receivedAt)
                  : null
              }
              onChange={(date) => {
                setValue(
                  'receivedAt',
                  date ? date.toDate() : undefined,
                )
              }}
              slotProps={{ textField: { fullWidth: true } }}
              disableFuture
            />
            {errors.receivedAt && <FormHelperText error>
              {errors.receivedAt.message}
            </FormHelperText>}
          </Grid>
          {renderFields([fields[4]])}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!useConnectedMemberAsReceiver}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setValue('useConnectedMemberAsReceiver', checked)
                  }}
                />
              }
              label={'J\'ai réceptionné la commande moi-même'}
            />
          </Grid>
          <Grid item xs={12} className="!pt-0">
            <ServerAutocomplete<MemberDto>
              fetchService={fetchMembers}
              onChange={(value: MemberDto | null) => {
                setValue('receivedByMemberId', value ? value.id : undefined)
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => {
                const member = value as MemberDto
                if (!member) return ''
                const roleLabel = MemberRoleEnumLabels[member.role]
                return member
                  ? `${member.firstName} ${member.lastName} ${roleLabel ? `(${roleLabel})` : ''}`
                  : ''
              }}
              debounceMs={300}
              placeholder="Rechercher un membre..."
              label="Membre ayant réceptionné"
              helperText={errors.receivedByMemberId?.message}
              error={!!errors.receivedByMemberId}
              minSearchLength={0}
              disabled={!!useConnectedMemberAsReceiver}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!useOrderSupplier}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setValue('useOrderSupplier', checked)
                  }}
                />
              }
              label={`Le fournisseur est bien celui de la commande (${productOrder.supplier.name})`}
            />
          </Grid>
          {renderFields([fields[0], fields[1]])}
        </Grid>

        <Grid container item xs={12} md={6} spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!watch('markOrderAsReceived')}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setValue('markOrderAsReceived', checked)
                  }}
                />
              }
              label={`Marquer la commande comme "${ProductOrderStatusLabels[ProductOrderStatus.RECEIVED]}"`}
            />
            <Alert severity="info" className="mb-4">
              {`Si décoché, la commande ${productOrder.status === ProductOrderStatus.PENDING_DELIVERY ? 'passera' : 'restera'} 
              en "${ProductOrderStatusLabels[ProductOrderStatus.PARTIALLY_RECEIVED]}".`}
            </Alert>
          </Grid>

          {renderFields([fields[2], fields[3]])}

          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              Bon de livraison
            </Typography>
            <SimpleFileInput
              file={deliveryNote}
              onSelect={(file) => {
                if (!file) {
                  dispatch(removeProductOrderReceiptFile({ type: 'deliveryNote' }))
                  return
                }
                dispatch(setProductOrderReceiptFile({
                  type: 'deliveryNote',
                  file,
                }))
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              Document de libération
            </Typography>
            <SimpleFileInput
              file={releaseCertificate}
              onSelect={(file) => {
                if (!file) {
                  dispatch(removeProductOrderReceiptFile({ type: 'releaseCertificate' }))
                  return
                }
                dispatch(setProductOrderReceiptFile({
                  type: 'releaseCertificate',
                  file,
                }))
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </WrapperFormContent>
  )
}

export default ProductOrderReceiptStepInfo
